import type { AgenticClientListItem } from "@p0security/integrations-agentic-shared/interface";
import { Spacing, Spin } from "@p0security/ui";
import { createContext, useEffect, useMemo, useState } from "react";

import { useMcpClients } from "./hooks";

export type AgenticClientsValue = {
  clients: AgenticClientListItem[];
  clientsById: Map<string, AgenticClientListItem>;
  error?: string;
  loading: boolean;
  refresh: () => void;
};

/** Registered MCP clients for the active tenant, plus a lookup keyed by id. */
export const AgenticClientsContext = createContext<AgenticClientsValue>({
  clients: [],
  clientsById: new Map(),
  loading: false,
  refresh: () => undefined,
});

/** Loads the tenant's MCP clients and exposes them, spinning until first
 * loaded. */
export const AgenticClientsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { items, loading, error, refresh } = useMcpClients();
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loading) setLoaded(true);
  }, [loading]);
  const value = useMemo(
    () => ({
      clients: items,
      clientsById: new Map(items.map((client) => [client.id, client])),
      error,
      loading,
      refresh,
    }),
    [items, error, loading, refresh]
  );
  // A refresh keeps the children mounted
  if (loading && !loaded)
    return (
      <Spacing asChild mt={64}>
        <Spin size="large" className="flex justify-center" />
      </Spacing>
    );
  return (
    <AgenticClientsContext.Provider value={value}>
      {children}
    </AgenticClientsContext.Provider>
  );
};
